import React, { useEffect, useState } from "react";
import { maintenanceApi, ProcManufacturer, SocketTypeItem } from "../../../api/maintenance";
import { errMsg } from "./procUtils";
import { useNotice } from "../shared/useNotice";

interface Props {
  onClose: () => void;
  onDone: (minted: number, batchName: string) => void;
}

/**
 * Mint N brand-new device identities in-app, replacing the standalone Python
 * script. IDs are checked for collision against both the pool and live devices.
 */
const MintBatchForm: React.FC<Props> = ({ onClose, onDone }) => {
  const [manufacturers, setManufacturers] = useState<ProcManufacturer[]>([]);
  const [socketTypes, setSocketTypes] = useState<SocketTypeItem[]>([]);
  const [quantity, setQuantity] = useState(100);
  const [manufacturerId, setManufacturerId] = useState("");
  const [hardwareModel, setHardwareModel] = useState("");
  const [poRef, setPoRef] = useState("");
  const [connectorStd, setConnectorStd] = useState("");
  const [powerKw, setPowerKw] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useNotice("error", "");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<{ ids: string[]; batch: string; minted: number } | null>(null);

  useEffect(() => {
    // Connector standards come from vehicles.charging_socket_type (the same
    // lookup the device form uses) rather than a list hardcoded here — a
    // hardcoded one silently goes stale and drifts from the real names.
    Promise.all([maintenanceApi.procGetManufacturers(), maintenanceApi.getSocketTypes()])
      .then(([m, s]) => {
        setManufacturers(m.data);
        // "Socket 0" is the device-level pseudo-connector the backend creates
        // automatically; it is not a connector standard you'd order a batch in.
        setSocketTypes(s.data.filter((t) => t.name !== "Socket 0"));
      })
      .catch(() => undefined);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (quantity < 1 || quantity > 500) {
      setError("Quantity must be between 1 and 500");
      return;
    }
    if (
      !window.confirm(
        `Mint ${quantity} device identit${quantity === 1 ? "y" : "ies"}?\n\n` +
          `Each gets a unique ID, QR URL and OCPP WebSocket URL, and lands in the pool as "generated".`
      )
    )
      return;

    setError("");
    setSaving(true);
    try {
      const res = await maintenanceApi.procMintBatch({
        quantity,
        manufacturer_id: manufacturerId ? Number(manufacturerId) : undefined,
        hardware_model: hardwareModel.trim() || undefined,
        po_ref: poRef.trim() || undefined,
        connector_std: connectorStd || undefined,
        power_kw: powerKw ? parseFloat(powerKw) : undefined,
        unit_cost: unitCost ? parseFloat(unitCost) : undefined,
        notes: notes.trim() || undefined,
      });
      setPreview({
        ids: res.data.unique_ids,
        batch: res.data.batch_name,
        minted: res.data.minted,
      });
    } catch (err: any) {
      setError(errMsg(err, "Failed to mint batch"));
      setSaving(false);
    }
  };

  if (preview) {
    return (
      <div className="maint-form-overlay" onClick={onClose}>
        <div className="maint-form-card maint-form-card--wide" onClick={(e) => e.stopPropagation()}>
          <div className="maint-form-header">
            <h3>Minted {preview.minted} devices</h3>
            <button className="maint-btn-icon" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="proc-banner good">
            ✅ {preview.minted} identit{preview.minted === 1 ? "y" : "ies"} added to{" "}
            <b>{preview.batch}</b>. Each QR is printed <b>with its device ID</b> onto a unit, and the
            same ID is flashed as the device's OCPP identity.
          </div>
          <div style={{ maxHeight: 220, overflow: "auto" }}>
            {preview.ids.slice(0, 200).map((id) => (
              <span key={id} className="proc-chip proc-mono">
                {id}
              </span>
            ))}
            {preview.ids.length > 200 && (
              <span className="proc-mini">+{preview.ids.length - 200} more</span>
            )}
          </div>
          <div className="proc-note">
            Select them in the pool and <b>Add to order</b> to send them to a manufacturer.
          </div>
          <div className="maint-form-actions">
            <button
              className="maint-btn-primary"
              onClick={() => onDone(preview.minted, preview.batch)}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="maint-form-overlay" onClick={onClose}>
      <div className="maint-form-card maint-form-card--wide" onClick={(e) => e.stopPropagation()}>
        <div className="maint-form-header">
          <h3>Mint a new device batch</h3>
          <button className="maint-btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="proc-mini" style={{ marginTop: 0 }}>
          Mints unique IDs in-app (replacing the offline Python script). Each gets a QR URL and an
          OCPP WebSocket URL, and lands in the pool as <b>generated</b>. IDs are checked for
          collision against both the pool and live devices.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="maint-form-row">
            <label>
              Quantity *
              <input
                type="number"
                min={1}
                max={500}
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
              />
            </label>
            <label>
              Manufacturer
              <select value={manufacturerId} onChange={(e) => setManufacturerId(e.target.value)}>
                <option value="">— none yet —</option>
                {manufacturers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="maint-form-row">
            <label>
              Hardware model
              <input
                value={hardwareModel}
                onChange={(e) => setHardwareModel(e.target.value)}
                placeholder="AC001 7.4kW"
              />
            </label>
            <label>
              PO / order ref
              <input value={poRef} onChange={(e) => setPoRef(e.target.value)} placeholder="PO-…" />
            </label>
          </div>

          <div className="maint-form-row">
            <label>
              Connector standard
              <select value={connectorStd} onChange={(e) => setConnectorStd(e.target.value)}>
                <option value="">— not specified —</option>
                {socketTypes.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Power (kW)
              <input
                type="number"
                step="any"
                value={powerKw}
                onChange={(e) => setPowerKw(e.target.value)}
              />
            </label>
            <label>
              Unit cost
              <input
                type="number"
                step="any"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
              />
            </label>
          </div>

          <label>
            Notes
            <input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          {error && <p className="maint-form-error">{error}</p>}

          <div className="maint-form-actions">
            <button type="button" className="maint-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="maint-btn-primary" disabled={saving}>
              {saving ? "Minting…" : `Mint ${quantity}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MintBatchForm;
